import React from "react";

type RichTextPart =
  | string
  | {
      text: string;
      strong?: boolean;
      linkUrl?: string;
    };

type LegalSubsection = {
  number: string;
  text: RichTextPart[] | string;
  bullets?: string[];
  tailParagraphs?: Array<RichTextPart[] | string>;
  subsections?: LegalSubsection[];
};

export type LegalSection = {
  title: string;
  paragraphs?: RichTextPart[];
  bullets?: Array<RichTextPart[] | string>;
  tailParagraphs?: Array<RichTextPart[] | string>;
  subsections?: LegalSubsection[];
};

type LegalDocumentViewProps = {
  sections: LegalSection[];
};

const toArray = <T,>(value: T[] | Record<string, T> | undefined) => {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : Object.values(value);
};

const isRichTextObject = (
  part: unknown
): part is Exclude<RichTextPart, string> => {
  return (
    typeof part === "object" &&
    part !== null &&
    "text" in part &&
    typeof (part as { text?: unknown }).text === "string"
  );
};

const LegalDocumentView: React.FC<LegalDocumentViewProps> = ({ sections }) => {
  const renderRichText = (value: unknown) => {
    if (typeof value === "string") {
      return value;
    }

    const parts = Array.isArray(value) ? value : [value];

    return (
      <>
        {parts.map((part, index) =>
          typeof part === "string" ? (
            <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
          ) : isRichTextObject(part) ? (
            part.linkUrl ? (
              <a
                key={`${part.text}-${index}`}
                href={part.linkUrl}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-800"
              >
                {part.text}
              </a>
            ) : part.strong ? (
              <strong key={`${part.text}-${index}`}>{part.text}</strong>
            ) : (
              <React.Fragment key={`${part.text}-${index}`}>
                {part.text}
              </React.Fragment>
            )
          ) : (
            <React.Fragment key={`invalid-${index}`} />
          )
        )}
      </>
    );
  };

  const renderSubsection = (subsection: LegalSubsection, depth = 0) => {
    const isNested = depth > 0;
    const isDeepNested = depth > 1;
    const nestedSpacing = depth === 1 ? "ml-4" : depth > 1 ? "ml-6" : "";
    const wrapperClassName = isNested
      ? isDeepNested
        ? `${nestedSpacing} ml-[1.05rem] pl-2 py-1.5`
        : `${nestedSpacing} rounded-xl border border-slate-200 bg-slate-50 px-4 py-3`
      : "rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm";

    return (
      <div key={subsection.number} className={wrapperClassName}>
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <p
            className={
              isDeepNested
                ? "shrink-0 text-sm font-semibold text-slate-950 md:text-sm"
                : isNested
                ? "shrink-0 text-sm font-semibold text-slate-950 md:text-base"
                : "shrink-0 text-base font-semibold text-slate-950 md:text-lg"
            }
          >
            {subsection.number}
          </p>
          <div className="min-w-0 flex-1">
            <p
              className={
                isDeepNested
                  ? "text-sm leading-7 text-slate-700 md:text-sm"
                  : isNested
                  ? "text-sm leading-7 text-slate-700 md:text-base"
                  : "text-base leading-8 text-slate-700 md:text-lg"
              }
            >
              {renderRichText(subsection.text)}
            </p>
          </div>
        </div>

        {subsection.bullets?.length ? (
          <ul
            className={
              isDeepNested
                ? "mt-2 space-y-1 pl-6 text-sm leading-7 text-slate-700 md:text-sm"
                : isNested
                ? "mt-3 space-y-2 pl-7 text-sm leading-7 text-slate-700 md:text-base"
                : "mt-4 space-y-2 pl-8 text-base leading-7 text-slate-700 md:text-lg"
            }
          >
            {subsection.bullets.map((bullet) => (
              <li key={bullet} className="list-disc">
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}

        {subsection.subsections?.length ? (
          <div className={isDeepNested ? "mt-1.5 space-y-1.5" : "mt-4 space-y-3"}>
            {subsection.subsections.map((nested) => renderSubsection(nested, depth + 1))}
          </div>
        ) : null}

        {subsection.tailParagraphs?.length ? (
          <div className={isDeepNested ? "mt-1.5 space-y-1.5" : "mt-4 space-y-3"}>
            {subsection.tailParagraphs.map((paragraph) => (
              <p
                key={
                  typeof paragraph === "string"
                    ? paragraph
                    : paragraph
                        .map((part) =>
                          typeof part === "string"
                            ? part
                            : isRichTextObject(part)
                              ? part.text
                              : ""
                        )
                        .join("|")
                }
                className={
                  isDeepNested
                    ? "text-sm leading-7 text-slate-800 md:text-sm"
                    : isNested
                    ? "text-sm leading-7 text-slate-700 md:text-base"
                    : "text-base leading-8 text-slate-700 md:text-lg"
                }
              >
                {renderRichText(paragraph)}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <article
          key={section.title}
          className="rounded-3xl border border-slate-200 bg-slate-50 p-5 md:p-6"
        >
          <h3 className="text-xl font-bold text-slate-950 md:text-2xl">
            {section.title}
          </h3>

          {section.paragraphs?.map((paragraph) => (
            <p
              key={typeof paragraph === "string" ? paragraph : paragraph.text}
              className="mt-4 text-base leading-8 text-slate-700 md:text-lg"
            >
              {typeof paragraph === "string" ? paragraph : renderRichText(paragraph)}
            </p>
          ))}

          {toArray(section.subsections).length ? (
            <div className="mt-5 space-y-4">
              {toArray(section.subsections).map((subsection) => (
                renderSubsection(subsection)
              ))}
            </div>
          ) : null}

          {section.bullets?.length ? (
            <ul className="mt-5 space-y-3 pl-5 text-base leading-7 text-slate-700 md:text-lg">
              {section.bullets.map((bullet) => (
                <li
                  key={
                    typeof bullet === "string"
                      ? bullet
                      : bullet
                          .map((part) =>
                            typeof part === "string"
                              ? part
                              : isRichTextObject(part)
                                ? part.text
                                : ""
                          )
                          .join("|")
                  }
                  className="list-disc"
                >
                  {renderRichText(bullet)}
                </li>
              ))}
            </ul>
          ) : null}

          {section.tailParagraphs?.length ? (
            <div className="mt-4 space-y-3">
              {section.tailParagraphs.map((paragraph) => (
                <p
                  key={
                    typeof paragraph === "string"
                      ? paragraph
                      : paragraph
                          .map((part) =>
                            typeof part === "string"
                              ? part
                              : isRichTextObject(part)
                                ? part.text
                                : ""
                          )
                          .join("|")
                  }
                  className="text-base leading-8 text-slate-700 md:text-lg"
                >
                  {renderRichText(paragraph)}
                </p>
              ))}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
};

export default LegalDocumentView;
